// src/lib/permissions/core/cache.ts
// Lightweight, efficient caching system for permission checks

export interface CacheStats {
  size: number;
  hitCount: number;
  missCount: number;
  hitRate: number;
}

/**
 * Lightweight permission cache with automatic cleanup
 * Optimized for frequent permission checks
 */
export class PermissionCache {
  private cache = new Map<string, any>();
  private hitCount = 0;
  private missCount = 0;
  private maxSize: number;

  constructor(maxSize = 100) {
    this.maxSize = maxSize;
  }

  /**
   * Get cached value or compute and cache it
   */
  get<T>(key: string, computeFn: () => T): T {
    if (this.cache.has(key)) {
      this.hitCount++;
      return this.cache.get(key) as T;
    }

    this.missCount++;
    const value = computeFn();
    this.set(key, value);
    return value;
  }
  /**
   * Set a value in the cache
   */
  set(key: string, value: any): void {
    // Simple LRU: if at max size, remove oldest entry
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, value);
  }

  /**
   * Check if key exists in cache
   */
  has(key: string): boolean {
    return this.cache.has(key);
  }

  /**
   * Remove specific key from cache
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
    this.hitCount = 0;
    this.missCount = 0;
  }

  /**
   * Get cache performance statistics
   */
  getStats(): CacheStats {
    const total = this.hitCount + this.missCount;
    return {
      size: this.cache.size,
      hitCount: this.hitCount,
      missCount: this.missCount,
      hitRate: total > 0 ? this.hitCount / total : 0
    };
  }
  /**
   * Invalidate cache entries by pattern
   */
  invalidatePattern(pattern: string): void {
    const keysToDelete: string[] = [];
    
    // Convert iterator to array to avoid downlevelIteration issues
    const keys = Array.from(this.cache.keys());
    for (const key of keys) {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Get current cache size
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Check if cache is at capacity
   */
  isFull(): boolean {
    return this.cache.size >= this.maxSize;
  }
}